class ListingView {
  constructor() {
    this.listingViewSelector = document.querySelector(
      "listing-view-selector[data-listing-view]",
    );
    if (!this.listingViewSelector) return;

    this.listingViewButtons = Array.from(
      this.listingViewSelector.querySelectorAll(".listing-view-selector__button"),
    );

    // Which view to show is decided by window.plpListingView, defined inline in
    // snippets/facets.liquid. It runs before the grid is parsed so the grid is
    // painted in the right view from the start; this deferred script used to
    // decide on its own, only after the whole page had parsed, and the grid
    // flashed the collection default first. Both now share those rules.
    this.rules = window.plpListingView;

    this.mobileImageSizes = {
      spotlight: "(min-width: 481px) calc(50vw - 1rem), calc(50vw - 0.5rem)",
      standard: "(min-width: 481px) calc(50vw - 1rem), calc(50vw - 0.5rem)",
      single: "(min-width: 481px) calc(100vw - 2rem), calc(100vw - 1rem)",
      grid: "(min-width: 481px) calc(25vw - 1.25rem), calc(25vw - 0.625rem)",
    };
    // In spotlight every 5th card spans the full row, so it needs full-width
    // images rather than the half-width default.
    this.fullSlotImageSizes = this.mobileImageSizes.single;

    this.clearLegacyPreference();

    // Cards whose spotlight slot has already been settled. See
    // continueSpotlightSlots.
    this.settledItems = new WeakSet();

    this.registerEvents();
  }

  // Older builds stored a single global preference in localStorage, which
  // survived browser restarts and overrode every collection's default
  // forever. Drop it so nobody stays stuck on a stale choice.
  clearLegacyPreference() {
    try {
      localStorage.removeItem("listingView");
    } catch (error) {}
  }

  registerEvents() {
    this.listingViewSelector.addEventListener("click", (event) => {
      const button = this.getViewButton(event.target);
      if (!button) return;

      const view = this.rules.selectable(button.dataset.view);
      this.rules.recordChoice(document.querySelector("#product-grid"), view);

      this.applyView(view, { notify: true });
    });

    this.applyView();

    document.addEventListener("product-grid-updated", () => {
      this.applyView(this.getStoredView(), { notify: true });
    });
  }

  getViewButton(target) {
    let node = target;
    while (node && node !== this.listingViewSelector) {
      if (
        node.classList &&
        node.classList.contains("listing-view-selector__button") &&
        this.listingViewSelector.contains(node)
      ) {
        return node;
      }
      node = node.parentNode;
    }

    return null;
  }

  getStoredView() {
    return this.rules.resolve(document.querySelector("#product-grid"));
  }

  // The server stamps each card's slot from its absolute position, assuming
  // every earlier page rendered a full page of products. With a filter active
  // the section drops unavailable products, so an earlier page can come up
  // short and the next page's stamps land out of rhythm — leaving a lone
  // half-width card with an empty space beside it at the page boundary.
  //
  // So cards appended after ones already on screen continue the rhythm from
  // the real previous card instead of trusting the server. Everything else
  // keeps its server stamp: the first render, a facet re-render, and pages
  // the scroll restore prepends above the current position — restamping those
  // would shift content the customer is already looking at.
  continueSpotlightSlots(productGrid) {
    const cycle = parseInt(productGrid.getAttribute("data-spotlight-cycle"), 10) || 5;
    let seenSettled = false;
    let sinceFull = null;

    Array.prototype.forEach.call(
      productGrid.querySelectorAll(":scope > li[data-plp-slot]"),
      (item) => {
        const settled = this.settledItems.has(item);

        if (!settled && seenSettled && sinceFull !== null) {
          item.setAttribute("data-plp-slot", sinceFull + 1 >= cycle ? "full" : "normal");
        }

        if (settled) seenSettled = true;
        if (item.getAttribute("data-plp-slot") === "full") {
          sinceFull = 0;
        } else if (sinceFull !== null) {
          sinceFull += 1;
        }

        this.settledItems.add(item);
      },
    );
  }

  updateImageSizes(productGrid, view) {
    const defaultSizes =
      this.mobileImageSizes[view] || this.mobileImageSizes[this.rules.fallback];

    Array.prototype.forEach.call(productGrid.querySelectorAll("img[sizes]"), (img) => {
      const currentSizes = img.getAttribute("sizes") || "";
      const desktopSizes = currentSizes.indexOf("(min-width: 990px) 1px") > -1
        ? "(min-width: 990px) 1px"
        : "(min-width: 990px) calc((100vw - 6rem) / 4)";

      let mobileSizes = defaultSizes;
      if (view === "spotlight") {
        const item = img.closest("#product-grid > li");
        if (item && item.getAttribute("data-plp-slot") === "full") {
          mobileSizes = this.fullSlotImageSizes;
        }
      }

      // Tablets (768-989px) always show 2 columns whatever the view, because
      // the switcher isn't available there. Without this entry they'd inherit
      // the view's mobile width and a grid collection would pick images half
      // the size they're displayed at.
      const tabletSizes = "(min-width: 768px) calc(50vw - 1.5rem)";

      img.setAttribute("sizes", `${desktopSizes}, ${tabletSizes}, ${mobileSizes}`);
    });
  }

  applyView(view = this.getStoredView(), options = {}) {
    // Renderable, not selectable — applyView has to be able to paint spotlight
    // when it's the collection default. While spotlight is showing, none of the
    // three buttons is active, which is accurate: the customer is in a view
    // none of them represents.
    const currentView =
      this.rules.renderable.indexOf(view) > -1 ? view : this.rules.fallback;
    const productGrid = document.querySelector("#product-grid");
    const previousView = productGrid ? productGrid.getAttribute("data-listing-view") : null;

    if (productGrid) {
      this.continueSpotlightSlots(productGrid);
      productGrid.setAttribute("data-listing-view", currentView);
      this.updateImageSizes(productGrid, currentView);
    }

    this.listingViewButtons.forEach((button) => {
      button.classList.toggle("is-active", button.dataset.view === currentView);
    });

    if (options.notify && productGrid && previousView !== currentView) {
      document.dispatchEvent(
        new CustomEvent("listing-view-changed", {
          detail: { view: currentView, previousView },
        }),
      );
    }
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => new ListingView());
} else {
  new ListingView();
}
